/**
 * Target Smart Editor Store Module
 *
 * Manages target document state and operations for mapping functionality
 *
 * Provides the following features:
 * - Target document content management
 * - Node selection and highlighting
 * - Source-target node mapping
 * - Content transfer handling
 * - Suggestion color management
 * - Element activation handling
 *
 * @module TargetSmartEditorStore
 */

import { mappingByType } from '../../../constants/generalization';
import { GeneralizationSuggestion, MappingTypeEnum } from '../../../types';
import { first, isEmpty, toNumber } from 'lodash';
import { ISmartEditorStore, SmartEditorStore, TargetNodeMap } from '..';
import { AUTHORING_COLORS, TEMPLATE_TRAINING_COLORS } from '../../../constants';
import { reaction } from 'mobx';
import { ClientInfo, EditorMode, IMappingSessionStore, UpdateMessageData } from '../../../stores';
import { sanitizeHtml } from '../../../helpers/documentComparisonHelpers';
import { getTableCellDataNodeIdsInRange } from '../utils';

/**
 * Target Smart Editor Store Interface
 * Defines contract for target document editor operations
 *
 * @interface ITargetSmartEditorStore
 * @extends {ISmartEditorStore}
 * @property {TargetNodeMap} targetNodeMap - Mapping between target and source nodes
 * @property {Function} getSourceId - Get source file ID for target node
 * @property {Function} dispose - Cleanup method for reactions
 */
export interface ITargetSmartEditorStore extends ISmartEditorStore {
  getSourceId: (targetNodeId: string) => number | undefined;
  dispose: () => void;
}

/**
 * Target Smart Editor Store
 * Handles target document state and highlighting
 *
 * @class TargetSmartEditorStore
 * @extends {SmartEditorStore}
 * @implements {ITargetSmartEditorStore}
 *
 * @example
 * // Basic usage
 * const store = new TargetSmartEditorStore(mappingSessionStore);
 *
 * @example
 * // Advanced usage with dropdown
 * const store = new TargetSmartEditorStore(
 *   mappingSessionStore,
 *   (sourceId) => updateDropdown(sourceId)
 * );
 */
export class TargetSmartEditorStore extends SmartEditorStore implements ITargetSmartEditorStore {
  /** Callback to update source file dropdown selection */
  private updateDropdownSelection?: (sourceId: string) => void;

  /** Mapping of target node IDs to their accepted suggestions */
  private targetNodeIdSuggestionMap: Record<string, GeneralizationSuggestion> = {};
  private sourceNodeIdSuggestionMap: Record<string, GeneralizationSuggestion> = {};

  /** List of disposer functions for MobX reactions */
  protected disposers: (() => void)[] = [];

  /** Store for element lock listeners */
  private lockListeners = new WeakMap<HTMLElement, (e: Event) => void>();

  constructor(
    mappingSessionStore: IMappingSessionStore,
    updateDropdownSelection?: (sourceId: string) => void,
  ) {
    super(mappingSessionStore);
    this.updateDropdownSelection = updateDropdownSelection;
    this.setupReactions();

    /** TODO: temp solution for source select comp and target store: refactor! */
    window.addEventListener('sourceFileSelected', ((e: CustomEvent) => {
      const detail = e.detail as { sourceId: number; bypassDocumentLock?: boolean };
      if (detail?.sourceId == null) return;
      this.mappingSessionStore.setSourceFileId(detail.sourceId, false, {
        bypassDocumentLock: detail.bypassDocumentLock ?? true,
      });
    }) as EventListener);
  }

  /**
   * Handle mapping data loaded event
   * Processes and highlights target nodes based on mapping data
   *
   * @method handleMappingDataLoaded
   * @returns {void}
   * @throws {Error} When mapping data is invalid
   */
  handleMappingDataLoaded = () => {
    const { targetNodeIdSuggestionMap, sourceNodeIdSuggestionMap } =
      this.mappingSessionStore.suggestionMaps;
    this.targetNodeIdSuggestionMap = targetNodeIdSuggestionMap;
    this.sourceNodeIdSuggestionMap = sourceNodeIdSuggestionMap;

    const nodeIds = this.getCellElementIds(this.targetNodeMap);
    const mergedNodeIds = this.getMergedEditsIds(nodeIds);
    this.highlightSuggestions(mergedNodeIds);
  };

  /**
   * Get source file ID for target node
   * Retrieves the source file ID from the first mapping suggestion
   *
   * @method getSourceId
   * @param {string} targetNodeId - Target node identifier
   * @returns {number | undefined} Source file ID if found
   * @throws {Error} When mapping is invalid
   *
   * @example
   * const sourceId = store.getSourceId('target-123');
   */
  getSourceId(targetNodeId: string): number | undefined {
    const suggestion = first(this.targetNodeMap?.[targetNodeId]);
    if (suggestion) {
      const firstKey = Object.keys(suggestion)[0];
      return suggestion[firstKey].sourceFileId;
    }

    if (!this.mappingSessionStore.sourceFileId) {
      return toNumber(this.mappingSessionStore.sourceFiles?.[0].id);
    }

    return;
  }

  /**
   * Handle element selection in target editor
   * Processes click events and manages element highlighting and source content
   *
   * @method handleElementSelection
   * @param {Element} element - Clicked DOM element
   * @returns {Promise<void>} Resolves when selection is processed
   * @throws {Error} When selection handling fails
   *
   * @example
   * await store.handleElementSelection(domElement);
   */
  /**
   * Select several table cells by node id (e.g. after drag-selecting a range across cells).
   * Reuses single/multi click logic so highlights, locks, and source sync stay consistent.
   */
  selectMultipleTableCells = (nodeIds: string[]) => {
    const doc = this.editorDocument;
    if (!doc || isEmpty(nodeIds)) {
      return;
    }

    const elements = nodeIds
      .map((id) => this.getElementByNodeId(id))
      .filter((el): el is Element => !!el);

    const unlocked = elements.filter((el) => {
      const id = el.getAttribute('data-nodeid') || '';
      return id && !this.mappingSessionStore.isLocked(id);
    });

    if (isEmpty(unlocked)) {
      return;
    }

    this.deactivateSelection();
    this.mappingSessionStore.setActiveNode('target', '', false);
    this.mappingSessionStore.setActiveNode('source', '', false);

    unlocked.forEach((el, index) => {
      this.handleElementSelection(el, index > 0);
    });

    const view = doc.defaultView;
    const domSel = view?.getSelection();
    if (domSel?.rangeCount) {
      domSel.removeAllRanges();
    }
  };

  protected tryHandleMultiCellTableSelection(range: Range): boolean {
    const ids = getTableCellDataNodeIdsInRange(range);
    if (ids.length <= 1) {
      return false;
    }
    this.mappingSessionStore.clearTextSelection();
    this.selectMultipleTableCells(ids);
    return true;
  }

  protected onPartialTextSelection(element: Element): void {
    const cell = this.resolveTableCell(element);
    const nodeId = cell.getAttribute('data-nodeid');
    if (!nodeId) {
      return;
    }

    cell.removeAttribute('data-highlight');
    cell.querySelectorAll('*').forEach((child) => {
      child.removeAttribute('data-highlight');
    });

    const color = this.getSuggestionColor(nodeId, cell);
    this.highlightElementWithChildren(cell, color ?? 'transparent');
  }

  handleElementSelection = (element: Element, isMultiSelect = false) => {
    const selectedElement = this.resolveTableCell(element);
    const nodeId = selectedElement?.getAttribute('data-nodeid');

    if (!nodeId) {
      return;
    }

    // Check if node is locked
    if (this.mappingSessionStore.isLocked(nodeId)) {
      return;
    }

    const currentTargetNodeIds = this.mappingSessionStore.activeNodeIds.get('target') || [];
    const isAlreadySelected = currentTargetNodeIds.includes(nodeId);

    // In multiselect mode, clicking an already selected cell should toggle it off.
    if (isMultiSelect && isAlreadySelected) {
      this.mappingSessionStore.setActiveNode('target', nodeId, true);
      this.deactivateSelection(nodeId);
      return;
    }

    const currentNodeId = this.getActiveElement()?.getAttribute('data-nodeid');
    if (!isMultiSelect && currentNodeId === nodeId) return;

    // Lock the node via WebSocket before making changes
    this.mappingSessionStore.lockNode(nodeId);

    if (!isMultiSelect) {
      this.deactivateSelection();
    }

    this.updateActiveNodes(nodeId, isMultiSelect);
    this.setActiveElement(selectedElement);
    this.updateSourceContent(nodeId, isMultiSelect);

    if (!this.isUserInteraction) {
      this.scrollToElement(selectedElement);
    }
  };

  deactivateSelection = (nodeId?: string) => {
    let activeNodes: Element[] = [];

    if (nodeId) {
      const activeNode = this.getElementByNodeId(nodeId);
      activeNode && activeNodes.push(activeNode);
    } else {
      activeNodes = Array.from(
        this.editorDocument?.querySelectorAll('[data-highlight="active"]') ?? [],
      );
    }

    if (isEmpty(activeNodes)) return;

    activeNodes.forEach((activeNode) => {
      const activeNodeId = activeNode.getAttribute('data-nodeid') || '';
      const color = this.getSuggestionColor(activeNodeId, activeNode);
      this.highlightElementWithChildren(activeNode, color);
      this.mappingSessionStore.unlockNode(activeNodeId);
    });
  };

  /**
   * Cleanup store resources
   * Disposes all mobx reactions and cleans up event handlers
   *
   * @method dispose
   * @returns {void}
   * @throws {Error} When disposal fails
   *
   * @example
   * // Cleanup when component unmounts
   * store.dispose();
   */
  dispose() {
    this.disposers.forEach((dispose) => dispose());
  }

  /**
   * Get cell element IDs from target node map
   * @protected
   */
  protected getCellElementIds(targetNodeMap: TargetNodeMap): string[] {
    const doc = this.editorDocument;
    if (!doc) return [];

    return Object.keys(targetNodeMap)
      .map((nodeId) => {
        const element = doc.querySelector(`[data-nodeid="${nodeId}"]`);
        return element && this.getCellElement(element);
      })
      .filter(Boolean)
      .map((cell) => cell?.getAttribute('data-nodeid') || '');
  }

  /**
   * Merge node IDs with accepted suggestions
   * @protected
   */
  protected getMergedEditsIds(nodeIds: string[]): string[] {
    if (!this.acceptedSuggestions) {
      return [];
    }

    const acceptedNodeIds = this.acceptedSuggestions
      .filter((suggestion) => suggestion.targetNodeId)
      .map((suggestion) => suggestion.targetNodeId);

    return [...new Set([...nodeIds, ...acceptedNodeIds])];
  }

  /**
   * Highlight suggestions in the document
   * @protected
   */
  protected highlightSuggestions(nodeIds: string[]) {
    const doc = this.editorDocument;
    if (!doc) return;

    // First apply all suggestion highlights
    nodeIds?.forEach((nodeId) => {
      const element = doc.querySelector(`[data-nodeid="${nodeId}"]`);
      if (element) {
        const activeElement = this.resolveTableCell(element);
        const highlightColor = this.getSuggestionColor(nodeId, element);
        this.highlightElement(activeElement, highlightColor);
      }
    });

    this.handleFirstHighlight();
  }

  /**
   * Get table cell element from node
   * @protected
   */
  protected getCellElement(element: Element): HTMLTableCellElement | null {
    const editorBody = this.editorDocument?.body;

    if (!editorBody) return null;

    const cell = element.closest('td, th');
    if (cell && editorBody.contains(cell)) {
      return cell as HTMLTableCellElement;
    }

    let currentNode: Node | null = element;
    while (currentNode && currentNode !== editorBody) {
      if (currentNode.nodeName === 'TD' || currentNode.nodeName === 'TH') {
        return currentNode as HTMLTableCellElement;
      }
      currentNode = currentNode.parentNode;
    }

    if (element.hasAttribute('data-nodeid')) {
      return element as HTMLTableCellElement;
    }

    return null;
  }

  /**
   * Get highlight color for a suggestion
   *
   * @protected
   * @param {string} nodeId - Node ID to check
   * @param {Element} element - Element to check
   * @returns {string} Color to use for highlighting
   */
  protected getSuggestionColor(nodeId?: string, element?: Element): string | undefined {
    if (!nodeId || !element) {
      return;
    }

    const modifiedNodeId = nodeId.replace('.html[0].body[1]', '');
    const authorEdits = this.mappingSessionStore.getTrackedChanges('target');
    if (authorEdits[nodeId] || authorEdits[modifiedNodeId]) {
      return AUTHORING_COLORS.NARRATIVE_SELECTION;
    }

    // Check direct suggestions first
    const color = this.getDirectSuggestionColor(nodeId, modifiedNodeId);
    if (color) return color;

    // Check child nodes if direct check fails
    return this.getChildNodesSuggestionColor(element);
  }

  /**
   * Get all node IDs within an element
   *
   * @protected
   * @param {Element} element - Element to check
   * @returns {string[]} Array of node IDs
   */
  protected getAllNodesIdsInElement(element: Element): string[] {
    const nodeIds: string[] = [];
    const elements = element.querySelectorAll('[data-nodeid]');
    elements.forEach((el) => {
      const nodeId = el.getAttribute('data-nodeid');
      if (nodeId) nodeIds.push(nodeId);
    });
    return nodeIds;
  }

  /**
   * Get color for accepted suggestion based on mapping type
   *
   * @protected
   * @param {string} mappingType - Type of mapping
   * @returns {string} Color for the suggestion
   */
  protected getColorForAcceptedSuggestion(mappingType: string): string {
    switch (mappingType) {
      case mappingByType.ai:
        return AUTHORING_COLORS.AI_ACCEPTED;
      case mappingByType.automapping:
        return TEMPLATE_TRAINING_COLORS.APPROVED;
      default:
        return AUTHORING_COLORS.MANUAL_ACCEPTED;
    }
  }

  /**
   * Get color for AI suggestion
   *
   * @protected
   * @returns {string} Color for the suggestion
   */
  protected getColorForAiSuggestion(): string {
    return this.mappingType === MappingTypeEnum.AUTO_MAPPING
      ? AUTHORING_COLORS.MANUAL_ACCEPTED
      : AUTHORING_COLORS.AI_SUGGESTION;
  }

  /**
   * Get color for child node suggestions
   * Checks nested nodes for suggestion matches
   *
   * @private
   * @param {Element} element - Element to check children of
   * @returns {string | null} Suggestion color or null if not found
   */
  private getChildNodesSuggestionColor(element: Element): string | undefined {
    const nodeIds = this.getAllNodesIdsInElement(element);
    if (!nodeIds.length) return;

    // Check accepted suggestions
    const acceptedChildSuggestion = this.acceptedSuggestions?.find((suggestion) =>
      nodeIds.includes(suggestion.targetNodeId),
    );
    if (acceptedChildSuggestion) {
      return this.getColorForAcceptedSuggestion(acceptedChildSuggestion.mapperType || '');
    }

    // Check AI suggestions
    const aiSuggestions = this.aiSuggestions;
    if (aiSuggestions && nodeIds.some((id) => id in aiSuggestions)) {
      return this.getColorForAiSuggestion();
    }
  }

  /**
   * Get color for direct node suggestions
   * Checks both accepted and AI suggestions for exact node matches
   *
   * @private
   * @param {string} nodeId - Original node ID
   * @param {string} modifiedNodeId - Node ID without HTML path
   * @returns {string | null} Suggestion color or null if not found
   */
  private getDirectSuggestionColor(nodeId: string, modifiedNodeId: string): string | null {
    // Check accepted suggestions
    const acceptedSuggestion =
      this.targetNodeIdSuggestionMap[nodeId] || this.targetNodeIdSuggestionMap[modifiedNodeId];

    if (acceptedSuggestion) {
      return this.getColorForAcceptedSuggestion(acceptedSuggestion.mapperType || '');
    }

    // Check AI suggestions
    if (
      this.aiSuggestions &&
      (nodeId in this.aiSuggestions || modifiedNodeId in this.aiSuggestions)
    ) {
      return this.getColorForAiSuggestion();
    }

    return null;
  }

  private updateActiveNodes(targetNodeId: string, isMultiSelect = false) {
    this.mappingSessionStore.setActiveNode('target', targetNodeId, isMultiSelect);

    if (isMultiSelect) {
      return;
    }

    // Check accepted suggestions
    const acceptedSuggestions = this.acceptedSuggestions?.filter(
      (suggestion) => suggestion.targetNodeId === targetNodeId,
    );

    if (acceptedSuggestions && acceptedSuggestions.length > 1) {
      // If multiple accepted suggestions, use all source node IDs
      const sourceNodeIds = acceptedSuggestions.map((suggestion) => suggestion.sourceNodeId);
      this.mappingSessionStore.setActiveNode(
        'source',
        sourceNodeIds[sourceNodeIds.length - 1] || '',
        isMultiSelect,
      );
      return;
    }

    // Then check AI suggestions if no accepted suggestions
    const aiSuggestion = this.aiSuggestions?.[targetNodeId]?.[0];
    if (aiSuggestion) {
      const sourceNodeIds = Object.keys(aiSuggestion);
      this.mappingSessionStore.setActiveNode(
        'source',
        sourceNodeIds[sourceNodeIds.length - 1] || '',
        isMultiSelect,
      );
      return;
    }

    // Fallback to existing suggestion map
    const sourceNodeId = this.targetNodeIdSuggestionMap[targetNodeId]?.sourceNodeId;
    this.mappingSessionStore.setDerivedSourceNodeId(sourceNodeId ?? '', isMultiSelect);
  }

  /**
   * Handle first highlighted element in document
   * Applies active highlighting and updates node mappings
   *
   * @private
   * @param {Document} doc - Document to search in
   */
  private handleFirstHighlight() {
    const doc = this.editorDocument;
    const partialSelectionActiveSpan = doc?.querySelector('[data-highlight="active"]');
    const element = doc?.querySelector('[data-highlighted="true"]');

    if (!element) {
      return;
    }

    const firstHighlightedElement = this.resolveTableCell(element);
    const nodeId = firstHighlightedElement.getAttribute('data-nodeid') ?? '';

    if (
      partialSelectionActiveSpan ||
      !firstHighlightedElement ||
      this.mappingSessionStore.lockedNodes[nodeId]
    ) {
      const sourceFileId = this.mappingSessionStore.sourceFiles?.[0].id;
      if (!this.mappingSessionStore.sourceFileId && sourceFileId) {
        this.mappingSessionStore.setSourceFileId(toNumber(sourceFileId));
      }
      return;
    }

    this.handleElementSelection(firstHighlightedElement);
  }

  /**
   * Update source content based on selection
   * Updates source file and dropdown when target node changes
   *
   * @method updateSourceContent
   * @private
   * @param {string} nodeId - Target node ID
   * @returns {Promise<void>} Resolves when source content is updated
   */
  private updateSourceContent(nodeId: string, isMultiSelect = false) {
    if (isMultiSelect) {
      return;
    }

    const sourceNodeId = first(this.mappingSessionStore.getSourceNodeIds(nodeId));

    const sourceId = this.mappingSessionStore.getSourceCatalogId(nodeId, sourceNodeId);
    if (!sourceId || this.mappingSessionStore.sourceFileId === sourceId) {
      this.mappingSessionStore.setDerivedSourceNodeId(sourceNodeId ?? '', isMultiSelect);
      return;
    }

    if (!this.mappingSessionStore.lockSourceDocumentSelection) {
      this.updateDropdownSelection?.(String(sourceId));
      this.mappingSessionStore.setSourceFileId(sourceId);
    }
    this.mappingSessionStore.setDerivedSourceNodeId(sourceNodeId ?? '', isMultiSelect);
  }

  /**
   * Handle editor mode updates when document is ready
   *
   * @private
   * @method handleEditorModeReaction
   * @param {Object} data - Reaction data
   * @param {EditorMode} data.editorMode - Current editor mode
   * @param {boolean} data.ready - Whether editor document is ready
   */
  private handleEditorModeReaction = ({
    editorMode,
    ready,
  }: {
    editorMode: EditorMode;
    ready: boolean;
  }) => {
    if (!ready) return;
    this.handleEditorModeChange(editorMode);
  };

  /**
   * Handle locked nodes updates when document is ready
   *
   * @private
   * @method handleLockedNodesReaction
   * @param {Object} data - Reaction data
   * @param {Record<string, ClientInfo>} data.lockedNodes - Map of locked nodes
   * @param {boolean} data.ready - Whether editor document is ready
   */
  private handleLockedNodesReaction = ({
    lockedNodes,
    ready,
  }: {
    lockedNodes: Record<string, ClientInfo>;
    ready: boolean;
  }) => {
    if (!ready) return;
    this.handleLockedNodes(lockedNodes);
  };

  /**
   * Handle target content updates when document is ready
   *
   * @private
   * @method handleContentReaction
   * @param {Object} data - Reaction data
   * @param {string} data.editorContent - New editor content
   * @param {boolean} data.ready - Whether editor document is ready
   */
  private handleContentReaction = ({
    editorContent,
    ready,
  }: {
    editorContent?: string;
    ready: boolean;
  }) => {
    if (!ready || !this.editorDocument) return;
    this.editorDocument.body.innerHTML = editorContent ?? '';
    this.handleMappingDataLoaded();
  };

  /**
   * Track locked nodes and editor readiness
   *
   * @private
   * @method trackLockedNodesState
   * @returns {Object} Current locked nodes state
   */
  private trackLockedNodesState = () => ({
    lockedNodes: this.mappingSessionStore.lockedNodes,
    ready: !!this.editorDocument && !!this.mappingSessionStore.webSocketClient,
  });

  /**
   * Track target content and editor readiness
   *
   * @private
   * @method trackContentState
   * @returns {Object} Current content state
   */
  private trackContentState = () => ({
    editorContent: this.mappingSessionStore.activeContent.get('target'),
    ready: !!this.editorDocument,
  });

  /**
   * Track editor mode and readiness
   *
   * @private
   * @method trackEditorModeState
   * @returns {Object} Current editor mode state
   */
  private trackEditorModeState = () => ({
    editorMode: this.mappingSessionStore.editorMode,
    ready: !!this.editorDocument,
  });

  /**
   * Setup MobX reactions for store state changes
   * Initializes reaction handlers for various store state changes
   *
   * @method setupReactions
   * @private
   * @returns {void}
   *
   * @remarks
   * Tracks and reacts to changes in:
   * - Editor mode changes (handleEditorModeReaction)
   * - Node locking state (handleLockedNodesReaction)
   * - Content updates (handleContentReaction)
   * - WebSocket message data (handleUpdatedData)
   *
   * @example
   * // Called internally during store initialization
   * private initialize() {
   *   this.setupReactions();
   * }
   *
   * @remarks
   * - Stores disposer functions for cleanup
   * - Critical for maintaining store reactivity
   * - Ensures proper state synchronization
   * - Must be disposed when store is destroyed
   */
  private setupReactions() {
    this.disposers.push(
      reaction(this.trackEditorModeState, this.handleEditorModeReaction),
      reaction(this.trackLockedNodesState, this.handleLockedNodesReaction),
      reaction(this.trackContentState, this.handleContentReaction),
      reaction(() => this.mappingSessionStore.updatedMessageData, this.handleUpdatedData),
    );
  }

  /**
   * Handle updated data from WebSocket messages
   * Updates target elements with new content from suggestions
   *
   * @method handleUpdatedData
   * @param {UpdateMessageData} [updatedData] - Data containing updated suggestions
   * @returns {void}
   *
   * @example
   * // When receiving WebSocket update
   * handleUpdatedData({
   *   suggestions: [{
   *     targetNodeId: "node-1",
   *     targetValue: "<p>updated content</p>"
   *   }]
   * });
   *
   * @remarks
   * - Creates efficient Map for suggestion lookups
   * - Updates target elements' content
   * - Sanitizes HTML content for security
   * - Highlights updated elements
   * - Skips non-existent elements
   * - Used in real-time collaboration
   */
  handleUpdatedData = (updatedData?: UpdateMessageData) => {
    if (!updatedData) {
      return;
    }
    // Create a Map for faster lookups
    const nodeMap = new Map(
      updatedData.suggestions.map((suggestion) => [suggestion.targetNodeId, suggestion]),
    );

    nodeMap.forEach((suggestion) => {
      const targetElement = this.getElementByNodeId(suggestion.targetNodeId);
      if (!targetElement) {
        return;
      }
      targetElement.innerHTML = sanitizeHtml(suggestion.targetValue);
    });
  };

  /**
   * Handle locked nodes updates
   * Updates visual state of locked nodes in the editor
   *
   * @private
   * @method handleLockedNodes
   * @param {Record<string, ClientInfo>} lockedNodes - Map of locked nodes
   * @returns {void}
   */
  private handleLockedNodes = (lockedNodes: Record<string, ClientInfo>) => {
    if (!this.editorDocument) return;

    // Get current target active node IDs
    const currentTargetNodeIds = this.mappingSessionStore.activeNodeIds.get('target') || [];

    if (currentTargetNodeIds.length > 0) {
      const remainingActiveNodes: string[] = [];
      const nodesToRemove: string[] = [];

      for (const nodeId of currentTargetNodeIds) {
        if (nodeId in lockedNodes) {
          remainingActiveNodes.push(nodeId);
        } else {
          nodesToRemove.push(nodeId);
        }
      }

      nodesToRemove.forEach((nodeId) => this.deactivateSelection(nodeId));

      // Update active nodes to only include those still in locked nodes
      remainingActiveNodes.length !== currentTargetNodeIds.length &&
        this.mappingSessionStore.activeNodeIds.set('target', remainingActiveNodes);
    }

    const filteredNewNodes = Object.entries(lockedNodes).reduce(
      (acc, [nodeId, lockInfo]) => {
        if (lockInfo.userId !== this.mappingSessionStore.clientInfo.userId) {
          acc[nodeId] = lockInfo;
        }
        return acc;
      },
      {} as Record<string, { userId: string; username: string }>,
    );

    // Restore all previously locked elements to original state
    this.editorDocument?.querySelectorAll('.locked-node').forEach((element) => {
      this.clearNodeLockStyles(element as HTMLElement);
    });

    // Update locked state for each node
    Object.entries(filteredNewNodes).forEach(([nodeId, { username }]) => {
      const element = this.editorDocument?.querySelector(`[data-nodeid="${nodeId}"]`);
      if (element) {
        this.setNodeLockStyles(element as HTMLElement, username);
      }
    });
  };

  /**
   * Apply lock styles to element
   * Sets visual indicators for locked state
   *
   * @private
   * @method setNodeLockStyles
   * @param {HTMLElement} element - Element to lock
   * @param {string} username - Name of user who locked the node
   * @returns {void}
   */
  private setNodeLockStyles(element: HTMLElement, username: string) {
    const lockedElement = this.resolveTableCell(element) as HTMLElement;
    const originalBgColor = lockedElement.style.backgroundColor;

    lockedElement.setAttribute('data-original-bg-color', originalBgColor);
    lockedElement.style.setProperty('background-color', 'rgba(128, 128, 128, 0.4)');
    lockedElement.style.cursor = 'not-allowed';
    lockedElement.style.userSelect = 'none';
    lockedElement.style.outline = 'none';
    lockedElement.classList.add('locked-node');
    lockedElement.setAttribute('title', `Locked by ${username}`);
    lockedElement.setAttribute('contenteditable', 'false');

    const preventSelection = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Store listener reference in WeakMap
    this.lockListeners.set(lockedElement, preventSelection);
    lockedElement.addEventListener('mousedown', preventSelection);
  }

  /**
   * Remove lock styles from element
   * Restores element to original state
   *
   * @private
   * @method clearNodeLockStyles
   * @param {HTMLElement} element - Element to unlock
   * @returns {void}
   */
  private clearNodeLockStyles(element: HTMLElement) {
    const originalBgColor = element.getAttribute('data-original-bg-color');
    element.style.backgroundColor = originalBgColor || '';
    element.removeAttribute('data-original-bg-color');
    element.style.cursor = '';
    element.style.userSelect = '';
    element.style.outline = '';
    element.classList.remove('locked-node');
    element.removeAttribute('title');
    element.removeAttribute('contenteditable');

    // Remove event listener using stored reference
    const listener = this.lockListeners.get(element);
    if (listener) {
      element.removeEventListener('mousedown', listener);
      this.lockListeners.delete(element);
    }
  }

  /**
   * Handle editor mode changes
   * Deactivates node when switching out of editing mode
   *
   * @private
   * @method handleEditorModeChange
   * @param {EditorMode} editorMode - New editor mode
   * @returns {void}
   */
  private handleEditorModeChange = (editorMode: EditorMode) => {
    if (editorMode !== EditorMode.EDITING) return;
    this.deactivateSelection();
  };

  /**
   * Re-apply background for a target node after author edits (overlay or inline in Editing mode).
   * Ensures DV/review yellow ({@link AUTHORING_COLORS.NARRATIVE_SELECTION}) is not cleared by
   * suggestion re-highlighting (which used to pass `transparent` for unmapped cells).
   */
  refreshAuthoringHighlightForNode = (nodeId: string) => {
    const doc = this.editorDocument;
    if (!doc) {
      return;
    }

    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(nodeId)
        : nodeId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const first = doc.querySelector(`[data-nodeid="${escaped}"]`);
    if (!first) {
      return;
    }

    const color = this.getSuggestionColor(nodeId, first) ?? 'transparent';
    const tableCell = this.resolveTableCell(first);
    if (tableCell) {
      this.highlightElement(tableCell, color);
      return;
    }

    let node: Element | null = first;
    while (node) {
      this.highlightElement(node as HTMLElement, color);
      const nextEl: Element | null = node.nextElementSibling;
      if (!nextEl || nextEl.hasAttribute('data-nodeid')) {
        break;
      }
      node = nextEl;
    }
  };
}
